const SCATTER_TOOLTIP_ESTIMATED_WIDTH_PX = 280;
const SCATTER_TOOLTIP_VIEWPORT_MARGIN_PX = 8;
const SCATTER_TOOLTIP_CURSOR_OFFSET_PX = 10;
const SCATTER_TOOLTIP_DEFAULT_LEFT_BIAS_PX = 100;

export interface ScatterTooltipPositionOptions {
  /** Tooltip width in CSS pixels; measured from DOM when omitted. */
  tooltipWidth?: number;
  cursorOffsetPx?: number;
  leftBiasPx?: number;
  /** Override for tests; defaults to visual viewport width. */
  viewportWidth?: number;
}

function getViewportWidth(): number {
  if (typeof window === "undefined") {return 1920;}
  return window.visualViewport?.width ?? window.innerWidth;
}

/** Width of the live scatter tooltip element when already rendered. */
export function measureScatterTooltipWidth(): number | undefined {
  if (typeof document === "undefined") {return undefined;}
  const el = document.getElementById("scatter-tooltip");
  if (!el) {return undefined;}
  const width = el.getBoundingClientRect().width;
  return width > 0 ? width : undefined;
}

/**
 * Default scatter tooltip sits slightly left of the cursor; flips fully left when
 * the tooltip would extend past the right viewport edge.
 */
export function computeScatterTooltipPosition(
  clientX: number,
  clientY: number,
  scaleFactor: number,
  options: ScatterTooltipPositionOptions = {},
): { x: number; y: number } {
  const sf = scaleFactor > 0 && Number.isFinite(scaleFactor) ? scaleFactor : 1;
  const cursorOffset = (options.cursorOffsetPx ?? SCATTER_TOOLTIP_CURSOR_OFFSET_PX) / sf;
  const leftBias = (options.leftBiasPx ?? SCATTER_TOOLTIP_DEFAULT_LEFT_BIAS_PX) / sf;

  let x = clientX + cursorOffset - leftBias;
  const y = clientY - cursorOffset;

  const viewportWidth = options.viewportWidth ?? getViewportWidth();
  const margin = SCATTER_TOOLTIP_VIEWPORT_MARGIN_PX;
  const tooltipWidth =
    options.tooltipWidth ?? measureScatterTooltipWidth() ?? SCATTER_TOOLTIP_ESTIMATED_WIDTH_PX;

  if (x + tooltipWidth > viewportWidth - margin) {
    x = clientX - tooltipWidth - cursorOffset;
  }

  if (x < margin) {
    x = margin;
  }

  return { x, y };
}
